/**
 * TriggerEditor — configuration panel for workflow trigger step nodes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, Zap, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { useActionEditorDraft } from '../../hooks/useActionEditorDraft';
import { resolveTriggerFromLegacy } from '../../utils/workflowTriggerCatalog';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const TriggerEditor = ({
  isOpen,
  onClose,
  step,
  onSave,
  workflowId,
  token,
  isNewWorkflow = false,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'trigger-editor',
  chromeHeight,
}) => {
  const [label, setLabel] = useState('');
  const [triggerKey, setTriggerKey] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!step) return;
    const config = step.config || {};
    const resolved = resolveTriggerFromLegacy(step);
    setLabel(resolved?.name || step.label || step.name || '');
    setTriggerKey(
      resolved?.eventKey
      || config.trigger_key
      || config.event_key
      || config.event
      || '',
    );
    setDescription(resolved?.description || step.description || config.description || '');
    setEnabled(config.enabled !== false);
  }, [step]);

  const draft = useMemo(
    () => ({ label, triggerKey, description, enabled }),
    [label, triggerKey, description, enabled],
  );
  const mapStepToDraft = useCallback((s) => {
    const config = s?.config || {};
    const resolved = resolveTriggerFromLegacy(s);
    return {
      label: resolved?.name || s?.label || s?.name || '',
      triggerKey: resolved?.eventKey || config.trigger_key || config.event_key || config.event || '',
      description: resolved?.description || s?.description || config.description || '',
      enabled: config.enabled !== false,
    };
  }, []);
  const { guardedClose, commitDraft } = useActionEditorDraft({
    editorId: step ? `trigger-${step.id}` : 'trigger',
    isOpen,
    step,
    draft,
    mapStepToDraft,
  });
  const requestClose = useCallback(() => guardedClose(onClose), [guardedClose, onClose]);

  if (!isOpen || !step) return null;

  const handleSave = async () => {
    const resolved = resolveTriggerFromLegacy({
      ...step,
      label: label.trim(),
      config: {
        ...(step.config || {}),
        trigger_key: triggerKey.trim(),
        event_key: triggerKey.trim(),
        event: triggerKey.trim(),
      },
    });
    const canonicalKey = resolved?.eventKey || triggerKey.trim();
    const canonicalLabel = resolved?.name || label.trim() || 'Trigger';
    const canonicalDescription = resolved?.description || description.trim();

    const updatedStep = {
      ...step,
      type: 'trigger',
      label: canonicalLabel,
      name: canonicalLabel,
      description: canonicalDescription,
      config: {
        ...(step.config || {}),
        trigger_key: canonicalKey,
        event_key: canonicalKey,
        event: canonicalKey,
        description: canonicalDescription,
        enabled,
        configured: true,
      },
    };

    if (!isNewWorkflow && workflowId && token) {
      setSaving(true);
      try {
        const res = await axios.patch(
          `${API}/platform/system-automations/workflows/${workflowId}/nodes/${step.id}`,
          {
            config: updatedStep.config,
            label: updatedStep.label,
            name: updatedStep.name,
            type: 'trigger',
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        onSave(res.data?.node || updatedStep);
        toast.success('Trigger saved');
        commitDraft();
        onClose();
      } catch (err) {
        const detail = err.response?.data?.detail;
        const message = Array.isArray(detail)
          ? detail.map((item) => item.msg || item).join(', ')
          : detail || 'Failed to save trigger';
        toast.error(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    onSave(updatedStep);
    toast.success('Trigger saved');
    commitDraft();
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="bg-zinc-900 border-l border-zinc-700"
      testId="trigger-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Zap size={20} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{label || 'Trigger'}</h3>
            <p className="text-xs text-zinc-500">Trigger</p>
          </div>
        </div>
        <button onClick={requestClose} className="p-2 hover:bg-zinc-800 rounded-lg" type="button">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-3">
          <p className="text-sm text-zinc-300">
            Configure how this workflow trigger step is labeled and described in the builder.
          </p>
          {triggerKey && (
            <p className="text-xs text-zinc-500 mt-2">
              Trigger key: <span className="text-zinc-400">{triggerKey}</span>
            </p>
          )}
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Trigger Name *</Label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            placeholder="Forgot password requested"
          />
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Trigger Key / Event Key</Label>
          <Input
            value={triggerKey}
            onChange={(e) => setTriggerKey(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            placeholder="forgot_password_requested"
          />
          <p className="text-xs text-zinc-500 mt-1">System event that starts or matches this trigger step.</p>
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Description</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-2 bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
            placeholder="Workflow trigger"
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-800/40 p-3">
          <div>
            <p className="text-sm text-white">Enabled</p>
            <p className="text-xs text-zinc-500">Include this trigger step in the workflow.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={requestClose} className="border-zinc-600 text-zinc-300" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white" disabled={saving || !label.trim()}>
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Trigger'
          )}
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default TriggerEditor;
